/**
 * File URL Utilities
 * Helper functions for handling file URLs in both local and GCS modes
 */

import { API_URL } from "../services/api";

/**
 * Check if a path is already a full URL
 */
export const isFullUrl = (path: string): boolean => {
  if (!path) return false;
  return path.startsWith("http://") || path.startsWith("https://");
};

/**
 * Get file URL for display (handles both local and GCS modes)
 *
 * @param path - File path from API (can be local path or signed URL)
 * @returns The URL to use for display
 */
export const getFileUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;

  // If already a full URL (GCS signed URL), use it directly
  if (isFullUrl(path)) {
    return path;
  }

  // Local mode: construct URL with API prefix
  return `${API_URL}/results/${path}`;
};

/**
 * Get filename from path or URL
 */
export const getFilenameFromPath = (path: string): string => {
  if (!path) return "";

  // Handle URLs
  if (isFullUrl(path)) {
    try {
      const urlObj = new URL(path);
      const pathname = urlObj.pathname;
      return pathname.split("/").pop() || "";
    } catch {
      return "";
    }
  }

  // Handle local paths
  return path.split("/").pop() || "";
};

/**
 * Handle file download (works for both local and GCS)
 */
export const downloadFile = (
  path: string | null | undefined,
  filename?: string
): void => {
  if (!path) return;

  const downloadUrl = getFileUrl(path);
  if (!downloadUrl) {
    throw new Error("Failed to get download URL");
  }

  // Create temporary link and trigger download
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename || getFilenameFromPath(path);
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
